import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Grid,
  styled,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f7f5f1",
    borderRadius: "14px",
    transition: "all 0.25s ease",
    "& fieldset": {
      borderColor: "#e8e3da",
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: "#c8b89c",
    },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 4px rgba(184, 154, 106, 0.10)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#b89a6a",
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: "#292722",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8b867d",
    opacity: 1,
  },
  "& .MuiInputLabel-root": {
    display: "none",
  },
  marginBottom: "4px",
});

const SuccessMessage = () => {
  const navigate = useNavigate();

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 680,
        mx: "auto",
        p: { xs: 4, md: 7 },
        borderRadius: "28px",
        border: "1px solid #ebe5dc",
        background:
          "linear-gradient(145deg, #ffffff 0%, #faf7f2 100%)",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 76,
          height: 76,
          mx: "auto",
          mb: 3,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f1eadf",
          color: "#9b7b4f",
        }}
      >
        <CheckCircleOutlineRoundedIcon sx={{ fontSize: 42 }} />
      </Box>

      <Typography
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: { xs: "30px", md: "40px" },
          fontWeight: 600,
          color: "#211f1b",
          mb: 1.5,
        }}
      >
        Thank You
      </Typography>

      <Typography
        sx={{
          color: "#777168",
          fontSize: "15px",
          lineHeight: 1.8,
          maxWidth: 500,
          mx: "auto",
          mb: 4,
        }}
      >
        Your request has been received successfully. Our support team will
        review your message and get back to you shortly.
      </Typography>

      <Button
        onClick={() => navigate("/")}
        endIcon={<ArrowForwardRoundedIcon />}
        sx={{
          px: 3.5,
          py: 1.4,
          borderRadius: "50px",
          backgroundColor: "#211f1b",
          color: "#fff",
          textTransform: "none",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "#3b3731",
            transform: "translateY(-2px)",
          },
          transition: "all 0.25s ease",
        }}
      >
        Return to Home
      </Button>
    </Paper>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    contact: "",
    email: "",
    issueType: "",
    description: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userId = sessionStorage.getItem("userId");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.contact.trim() ||
      !formData.email.trim() ||
      !formData.issueType ||
      !formData.description.trim()
    ) {
      setErrorMessage("Please complete all fields before submitting.");
      return;
    }

    const payload = {
      contact: formData.contact.trim(),
      email: formData.email.trim(),
      issueType: formData.issueType,
      description: formData.description.trim(),
    };

    if (formData.issueType !== "Login Issue" && userId) {
      payload.userId = userId;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const response = await fetch(
        "https://backend.hindustanwardrobe.in/user/createSupportTicket",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(
          result.message || "Failed to submit. Please try again."
        );
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      <Box sx={{ backgroundColor: "#fff", color: "#211f1b" }}>
        <Box
          sx={{
            background: "#F6F3EE",
            borderBottom: "1px solid #DDD7CE",
            py: { xs: 7, md: 11 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -100,
              right: -100,
              width: 320,
              height: 320,
              borderRadius: "50%",
              border: "1px solid rgba(150,120,70,0.18)",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              bottom: -180,
              left: -120,
              width: 360,
              height: 360,
              borderRadius: "50%",
              border: "1px solid rgba(150,120,70,0.12)",
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Grid container spacing={5} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography
                  sx={{
                    fontSize: 12,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "#9A7944",
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Hindustan Wardrobe · Customer Care
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: { xs: 46, sm: 64, md: 82 },
                    lineHeight: 0.95,
                    fontWeight: 400,
                    color: "#171717",
                    maxWidth: 700,
                    mb: 3,
                  }}
                >
                  We're here
                  <br />
                  <Box component="span" sx={{ fontStyle: "italic" }}>
                    to help.
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 560,
                    color: "#68635C",
                    fontSize: { xs: 15, md: 17 },
                    lineHeight: 1.8,
                  }}
                >
                  From choosing the perfect outfit to questions about your rental,
                  delivery or return, our support team is here to make your wardrobe
                  experience effortless.
                </Typography>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    background: "#171717",
                    color: "#fff",
                    p: { xs: 4, md: 5 },
                    minHeight: 250,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    position: "relative",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.12)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: "#C5A46D",
                    }}
                  >
                    Customer Support
                  </Typography>

                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: { xs: 28, md: 34 },
                        mb: 1,
                      }}
                    >
                      Need assistance?
                    </Typography>

                    <Typography
                      sx={{
                        color: "#BDBDBD",
                        fontSize: 14,
                        lineHeight: 1.7,
                        maxWidth: 350,
                      }}
                    >
                      Send us your query and our team will get back to you shortly.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 45,
                      height: 1,
                      background: "#C5A46D",
                      mt: 3,
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box
          sx={{
            py: { xs: 7, md: 11 },
            background:
              "linear-gradient(180deg, #fff 0%, #faf8f4 100%)",
          }}
        >
          <Container maxWidth="lg">
            {isSubmitted ? (
              <SuccessMessage />
            ) : (
              <Grid
                container
                spacing={{ xs: 5, md: 9 }}
                alignItems="center"
              >
                <Grid item xs={12} md={5}>
                  <Box sx={{ maxWidth: 440 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        letterSpacing: "3px",
                        textTransform: "uppercase",
                        color: "#a0825a",
                        fontWeight: 700,
                        mb: 2,
                      }}
                    >
                      Customer Care
                    </Typography>

                    <Typography
                      component="h2"
                      sx={{
                        fontFamily: '"Playfair Display", Georgia, serif',
                        fontSize: { xs: "38px", md: "52px" },
                        lineHeight: 1.1,
                        fontWeight: 500,
                        color: "#211f1b",
                        mb: 2.5,
                      }}
                    >
                      Let’s talk about your wardrobe.
                    </Typography>

                    <Typography
                      sx={{
                        color: "#756f66",
                        fontSize: "15px",
                        lineHeight: 1.9,
                        mb: 4,
                      }}
                    >
                      Whether you need help choosing an outfit, have a
                      question about a booking, or simply want to know more
                      about Hindustan Wardrobe, send us a message.
                    </Typography>

                    <Stack spacing={2.2}>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            borderRadius: "12px",
                            backgroundColor: "#f0ebe3",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#8f7048",
                          }}
                        >
                          <SupportAgentOutlinedIcon />
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#28251f",
                              mb: 0.3,
                            }}
                          >
                            Personal Assistance
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#817a71",
                            }}
                          >
                            We’re happy to help with your rental journey.
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            borderRadius: "12px",
                            backgroundColor: "#f0ebe3",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#8f7048",
                          }}
                        >
                          <MailOutlineOutlinedIcon />
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#28251f",
                              mb: 0.3,
                            }}
                          >
                            Quick Response
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#817a71",
                            }}
                          >
                            Share your concern and our team will respond.
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            borderRadius: "12px",
                            backgroundColor: "#f0ebe3",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#8f7048",
                          }}
                        >
                          <ContactSupportOutlinedIcon />
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: "#28251f",
                              mb: 0.3,
                            }}
                          >
                            Rental Support
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#817a71",
                            }}
                          >
                            Booking and rental-related assistance.
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Paper
                    component="form"
                    onSubmit={handleSubmit}
                    elevation={0}
                    sx={{
                      p: { xs: 3, sm: 4, md: 5 },
                      borderRadius: "26px",
                      backgroundColor: "#fff",
                      border: "1px solid #ebe6de",
                      boxShadow:
                        "0 20px 60px rgba(36,31,24,0.08)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Playfair Display", Georgia, serif',
                        fontSize: "28px",
                        fontWeight: 500,
                        mb: 0.8,
                      }}
                    >
                      Send a Message
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#8a847b",
                        mb: 3.5,
                      }}
                    >
                      Fill in your details and tell us how we can assist.
                    </Typography>

                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={2.2}>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          required
                          name="contact"
                          placeholder="Contact / Phone Number"
                          value={formData.contact}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          fullWidth
                          required
                          name="email"
                          placeholder="Email Address"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          required
                          name="issueType"
                          select
                          value={formData.issueType}
                          onChange={handleChange}
                          SelectProps={{
                            native: true,
                          }}
                        >
                          <option value="" disabled>
                            Select Issue Type
                          </option>
                          <option value="General">General</option>
                          <option value="Login Issue">Login Issue</option>
                          <option value="Booking Related">
                            Booking Related
                          </option>
                          <option value="Other">Other</option>
                        </StyledTextField>
                      </Grid>

                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          required
                          name="description"
                          placeholder="Tell us how we can help..."
                          multiline
                          minRows={5}
                          value={formData.description}
                          onChange={handleChange}
                        />
                      </Grid>

                      {errorMessage && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.5,
                              borderRadius: "12px",
                              backgroundColor: "#fff3f2",
                              border: "1px solid #ffd9d6",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#c62828",
                              }}
                            >
                              {errorMessage}
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          fullWidth
                          disabled={isSubmitting}
                          endIcon={<ArrowForwardRoundedIcon />}
                          sx={{
                            mt: 1,
                            minHeight: 52,
                            borderRadius: "14px",
                            backgroundColor: "#211f1b",
                            color: "#fff",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 700,
                            letterSpacing: "0.3px",
                            "&:hover": {
                              backgroundColor: "#3a352e",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#a9a49d",
                              color: "#fff",
                            },
                          }}
                        >
                          {isSubmitting ? "Sending..." : "Send Message"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Container>
        </Box>

        <Box
          sx={{
            py: { xs: 6, md: 8 },
            backgroundColor: "#211f1b",
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Container maxWidth="md">
            <Typography
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: { xs: "28px", md: "38px" },
                fontWeight: 500,
                mb: 1.5,
              }}
            >
              Your style deserves attention.
            </Typography>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.65)",
                fontSize: "14px",
                lineHeight: 1.8,
              }}
            >
              Rent. Wear. Return. And make every occasion memorable.
            </Typography>
          </Container>
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default Contact;